import type { InventoryItem } from '@/types/inventory'

// Helpers para elegir el primer valor definido y numérico
const pick = <T>(obj: any, keys: string[], fallback?: T): T | undefined => {
  for (const k of keys) {
    if (obj?.[k] !== undefined && obj?.[k] !== null) return obj[k]
  }
  return fallback
}

const pickNumber = (obj: any, keys: string[], fallback = 0): number => {
  const v = pick<any>(obj, keys)
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

const pickString = (obj: any, keys: string[], fallback = ''): string => {
  const v = pick<any>(obj, keys)
  return (typeof v === 'string' && v) ? v : fallback
}

/**
 * Normaliza un registro de inventario central desde múltiples variantes de backend.
 * Centraliza alias para evitar duplicar la lógica en el servicio.
 */
export function normalizeCentralInventoryItem(raw: any): InventoryItem {
  // Identificadores de origen (enterprise vs legacy) si vienen del backend unificado
  const uid: string | undefined = typeof raw?.uid === 'string' ? raw.uid : undefined
  const source: string | undefined = typeof raw?.source === 'string' ? raw.source : undefined
  const code = pickString(raw, ['code', 'item_code'])
  const name = pickString(raw, ['name'], '—')
  const description = pickString(raw, ['description'], '')
  const category = pickString(raw, ['category'], 'GENERAL') as any

  // Cantidades
  const available = pickNumber(raw, ['availableStock', 'available_quantity', 'currentStock'], 0)
  const reserved = pickNumber(raw, ['reservedStock', 'reserved_quantity', 'inTransit', 'in_transit'], 0)
  const inTransit = pick(raw, ['inTransit', 'in_transit'])
  // QC balances (si el backend los expone en data.qc)
  const reviewStock = pickNumber(raw.qc || raw, ['reviewStock'], 0)
  const quarantinedStock = pickNumber(raw.qc || raw, ['quarantinedStock'], 0)

  // Costos
  const unitPrice = pickNumber(raw, ['unitPrice', 'unit_cost'], 0)

  // Umbrales (mín/max) - coercion explícita para no perder valores si llegan como string
  const minStock = pickNumber(raw, ['minStock', 'minimum_stock', 'min_stock'], 0)
  const maxStockRaw = pick(raw, ['maxStock', 'maximum_stock', 'max_stock'])
  let maxStock: number | null = null
  if (maxStockRaw !== undefined && maxStockRaw !== null && maxStockRaw !== '') {
    const n = Number(maxStockRaw)
    if (Number.isFinite(n)) maxStock = n
  }

  // Fechas
  const lastUpdated = pickString(raw, ['lastUpdated', 'lastUpdate', 'updatedAt'], new Date().toISOString())
  const createdAt = pickString(raw, ['createdAt'], new Date().toISOString())

  // Ubicación
  const locationRaw = raw.location
  const location = locationRaw ? {
    agency: locationRaw.agency || locationRaw,
    warehouse: locationRaw.warehouse || 'Principal',
    shelf: locationRaw.shelf || '',
    zone: locationRaw.zone || ''
  } : {
    agency: 'Agencia Central',
    warehouse: 'Principal',
    shelf: '',
    zone: ''
  }

  // Valor total: derivado en frontend, pero calculamos un aproximado consistente
  const totalValue = (available + reserved) * unitPrice

  const normalized: InventoryItem = {
    id: raw.id,
    ...(uid ? ({ uid } as any) : {}),
    ...(source ? ({ source } as any) : {}),
    code,
    name,
    description,
    category,
    currentStock: Number.isFinite(raw.currentStock) ? raw.currentStock : available,
    availableStock: available,
    reservedStock: reserved,
    ...(inTransit !== undefined ? { inTransitStock: Number(inTransit) || 0 } : {}),
    reviewStock,  // ✅ FIX: Siempre incluir (puede ser 0)
    quarantinedStock,  // ✅ FIX: Siempre incluir (puede ser 0)
    // Siempre incluir minStock (ya normalizado numéricamente)
    minStock,
    // Incluir siempre maxStock (null si no definido) para que los formularios con v-model sean reactivos
    maxStock: maxStock !== null ? maxStock : null,
    unitPrice,
    totalValue: Number.isFinite(raw.totalValue) ? Number(raw.totalValue) : totalValue,
    status: raw.status,
    location,
    lastUpdated,
    createdAt,
    image: raw.image || '/default-product.png'
  }

  return normalized
}
